import { Button } from "@/v2-components/ui/button";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import Image from "next/image";
import Link from "next/link";
import { highlightHtml } from "@/components/tactsuit/tactglove-dk3/highlightHtml";

const CARD_SHADOW = "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.15)]";

type MediaProps = {
  src: string;
  alt: string;
  className?: string;
  /** 데스크탑/모바일 영상이 모두 주어지면 영상으로 대체 렌더. */
  videoDesktopSrc?: string;
  videoMobileSrc?: string;
};

const Media = ({
  src,
  alt,
  className,
  videoDesktopSrc,
  videoMobileSrc,
}: MediaProps) => (
  <div
    className={cn(
      "relative aspect-square w-full overflow-hidden rounded-2xl border border-coolgray-20 bg-coolgray-10 laptop:aspect-auto laptop:h-[586px] laptop:rounded-none laptop:border-0",
      className,
    )}
  >
    {videoDesktopSrc && videoMobileSrc ? (
      <>
        <video
          src={videoMobileSrc}
          className="absolute inset-0 h-full w-full object-cover laptop:hidden"
          autoPlay
          loop
          muted
          playsInline
          aria-label={alt}
        />
        <video
          src={videoDesktopSrc}
          className="absolute inset-0 hidden h-full w-full object-cover laptop:block"
          autoPlay
          loop
          muted
          playsInline
          aria-label={alt}
        />
      </>
    ) : (
      <Image src={src} alt={alt} fill className="object-cover" />
    )}
  </div>
);

export default function Dk3Technology() {
  const { t } = useTrans(["tactsuit"]);

  return (
    <section className="w-full bg-white px-4">
      {/* Header — 모바일: 타이틀만 (Figma 모바일에 description 없음) */}
      <header className="mx-auto mb-6 flex max-w-[708px] flex-col items-center gap-4 text-center laptop:mb-10">
        <h2 className="text-2xl font-400 leading-8 text-coolgray-90 laptop:text-[32px] laptop:leading-10">
          {t("Turn Your Palm Into a Haptic Canvas")}
        </h2>
        <p className="hidden text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-60 laptop:block laptop:text-base laptop:leading-[22px] laptop:tracking-[0.5px]">
          {t(
            "Experience continuous, flowing textures across your hand and crisp, console-quality impacts on your wrist. Elevate tactile immersion with our most advanced hardware yet.",
          )}
        </p>
      </header>

      {/* Body — 모바일 gap-16(64px), 데스크톱 **gap-24(96px)**.
          데스크톱 값은 Figma `28718:171908`(Turn Your Palm) 의 `Overviews` 실측이다:
            Item1 y=0 h=586 → Item2 y=682        → 96
            Item2 y=682 h=586(=1268) → Section y=1364 → 96
          종전 `laptop:gap-6`(24)은 근거 없이 좁았다(실측으로 24 확인). 모바일은 시안
          노드가 따로 있어 기존 64 를 유지한다 — Figma 모바일은 카드 wrap 없이 plain
          item 나열이다.
          ⚠️ 이 파일의 다른 `laptop:gap-6` 3곳(144·162·243)은 **카드 내부** 레이아웃이라
          섹션 간격과 무관하다. 함께 바꾸지 말 것. */}
      <div className="mx-auto flex w-full max-w-screen-max flex-col gap-16 laptop:gap-24">
        {/* Section 1: Seamless Spatiotemporal Feedback */}
        <div
          className={cn(
            "flex flex-col items-stretch gap-6",
            "laptop:bg-coolgray-0 laptop:gap-0 laptop:overflow-hidden laptop:rounded-2xl laptop:border-none laptop:border-coolgray-20",
            `laptop:${CARD_SHADOW} laptop:grid laptop:grid-cols-[1fr_1fr]`,
          )}
        >
          <Media
            src="/images/tactsuit/tactglove-dk3/tech-lra.jpg"
            videoDesktopSrc="/media/tactsuit/tactglove-dk3/tech-lra-desktop.mp4"
            videoMobileSrc="/media/tactsuit/tactglove-dk3/tech-lra-mobile.mp4"
            alt="7 high-definition LRAs visualization"
          />
          <div className="flex items-center laptop:justify-center laptop:px-10 laptop:py-10">
            <div className="flex max-w-[540px] flex-col items-start gap-3 laptop:gap-5">
              <h3 className="text-2xl font-400 leading-8 text-coolgray-90 laptop:text-[32px] laptop:leading-10">
                {t("Seamless Spatiotemporal Feedback")}
              </h3>
              <p
                className="text-sm leading-[18px] tracking-[0.25px] text-coolgray-60 laptop:text-base laptop:leading-[22px] laptop:tracking-[0.5px]"
                dangerouslySetInnerHTML={{
                  __html: highlightHtml(
                    t(
                      "Each of the 7 high-definition LRAs is independently amplitude-controlled via the SDK. Our patented tactile-illusion algorithm interpolates between adjacent motors, allowing the brain to perceive a single, continuous sensation rather than seven isolated points.",
                    ),
                  ),
                }}
              />
            </div>
          </div>
        </div>

        {/* Section 2: Sharp. Crisp. Precise. — 데스크톱: 텍스트 좌 / 이미지 우 (order reverse) */}
        <div
          className={cn(
            "flex flex-col items-stretch gap-6",
            "laptop:bg-coolgray-0 laptop:gap-0 laptop:overflow-hidden laptop:rounded-2xl laptop:border-none laptop:border-coolgray-20",
            `laptop:${CARD_SHADOW} laptop:grid laptop:grid-cols-[1fr_1fr]`,
          )}
        >
          <Media
            src="/images/tactsuit/tactglove-dk3/tech-vcm.jpg"
            videoDesktopSrc="/media/tactsuit/tactglove-dk3/tech-vcm-desktop.mp4"
            videoMobileSrc="/media/tactsuit/tactglove-dk3/tech-vcm-mobile.mp4"
            alt="DRAKE LF VCM module"
            className="laptop:order-2"
          />
          <div className="flex items-center laptop:order-1 laptop:justify-center laptop:px-10 laptop:py-10">
            <div className="flex w-full max-w-[540px] flex-col items-start gap-3 laptop:gap-20">
              <div className="flex flex-col gap-3 laptop:gap-5">
                <h3 className="text-2xl font-400 leading-8 text-coolgray-90 laptop:text-[32px] laptop:leading-10">
                  {t("Sharp. Crisp. Precise.")}
                </h3>
                <p
                  className="text-sm leading-[18px] tracking-[0.25px] text-coolgray-60 laptop:text-base laptop:leading-[22px] laptop:tracking-[0.5px]"
                  dangerouslySetInnerHTML={{
                    __html: highlightHtml(
                      t(
                        "The wrist haptics is driven by a high-fidelity Voice Coil Motor (from TITAN Haptics). By decoupling frequency from intensity, it reproduces the sharp, crisp feedback previously exclusive to dedicated console controllers.",
                      ),
                    ),
                  }}
                />
              </div>
              {/* SpecBlocks: 모바일 세로 + border-b 구분선 / 데스크톱 가로 3-col */}
              <div className="flex w-full flex-col laptop:flex-row laptop:items-start laptop:gap-6">
                <SpecBlock
                  label={t("FREQUENCY")}
                  value="0 – 100 Hz"
                  caption={t("independently controllable")}
                />
                <SpecBlock
                  label={t("FEEL")}
                  value={t("Sharp · Crispy")}
                  caption={t("controller-grade")}
                  isLast
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Sketch (모바일 단독 / 데스크톱 Sketch+Drop 2-col) */}
        <div className="flex flex-col gap-16 laptop:flex-row laptop:gap-6">
          {/* Sketch */}
          <div
            className={cn(
              "flex flex-col items-start gap-6",
              "laptop:min-h-[360px] laptop:basis-2/3 laptop:flex-row laptop:items-stretch laptop:justify-between laptop:gap-10 laptop:overflow-hidden laptop:rounded-2xl laptop:border-none laptop:border-coolgray-20 laptop:bg-coolgray-10 laptop:p-10 laptop:pr-0",
              `laptop:${CARD_SHADOW}`,
            )}
          >
            {/* 모바일 미디어 (상단). 데스크톱에선 우측 컬럼에서 렌더. */}
            <div className="relative aspect-[498/281] w-full overflow-hidden rounded-2xl border border-coolgray-20 bg-coolgray-10 laptop:hidden">
              <Image
                src="/images/tactsuit/tactglove-dk3/tech-sketch.jpg"
                alt="TactGlove Haptics authoring tool"
                fill
                className="object-cover"
              />
            </div>
            <div className="flex max-w-[480px] flex-col gap-6 laptop:w-[360px] laptop:max-w-none laptop:shrink-0 laptop:justify-between laptop:gap-0">
              <div className="flex flex-col gap-3 laptop:gap-4">
                <h3 className="text-2xl font-400 leading-8 text-coolgray-90">
                  {t("Sketch Haptics Instantly.")}
                </h3>
                <p className="block text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-60 laptop:hidden">
                  {t(
                    "Design spatiotemporal haptic patterns without a single line of code. Simply click and drag to define intensity across the timeline for all haptic points. Map motor flows and test results on your own hand instantly.",
                  )}
                </p>
                <p className="hidden text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-90 laptop:block">
                  {t(
                    "Our dedicated authoring tool lets you design spatiotemporal haptic patterns without writing a single line of code. Simply click and drag to define intensity and duration across a timeline for all 8 haptic points. Map the flow between motors and test the results on your own hand instantly.",
                  )}
                </p>
              </div>
              <Link href="/software/designer" className="self-start">
                <Button
                  variant="blue-fill"
                  shape="pill"
                  size="none"
                  className="h-10 px-6 text-xs font-600 leading-4 tracking-[0.1px]"
                >
                  {t("Learn More")}
                </Button>
              </Link>
            </div>
            {/* 데스크톱 미디어 (498×281 고정. 카드의 overflow-hidden 으로 우측 108 클리핑 → 가시 영역 390×281). */}
            <div className="relative hidden h-[281px] w-[498px] shrink-0 overflow-hidden rounded-l-lg laptop:block">
              <Image
                src="/images/tactsuit/tactglove-dk3/tech-sketch.jpg"
                alt="TactGlove Haptics authoring tool"
                fill
                className="object-cover"
              />
            </div>
          </div>

          {/* Drop */}
          <div
            className={cn(
              "flex flex-col items-start gap-3",
              "laptop:min-h-[360px] laptop:flex-1 laptop:justify-between laptop:overflow-hidden laptop:rounded-2xl laptop:border-none laptop:border-coolgray-20 laptop:bg-coolgray-10 laptop:p-10",
              `laptop:${CARD_SHADOW}`,
            )}
          >
            <div className="flex flex-col gap-3 laptop:gap-4">
              <h3 className="text-2xl font-400 leading-8 text-coolgray-90">
                {t("Drop Into Your Engine.")}
              </h3>
              <p
                className="text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-60"
                dangerouslySetInnerHTML={{
                  __html: highlightHtml(
                    t(
                      "Plug in with official Unity and Unreal Engine plugins for instant setup. Additional Python, JavaScript, and Android SDKs are available for custom integrations. Integrate real-time haptics into your ecosystem with minimal friction.",
                    ),
                  ),
                }}
              />
            </div>
            {/* 로고: 모바일 1행 (4 로고 한 줄), 데스크톱 2x2 grid */}
            <div className="w-full laptop:max-w-72 laptop:basis-1/3">
              <div className="flex flex-wrap gap-4 laptop:gap-6">
                <Image
                  src="/images/tactsuit/tactglove-dk3/unity.svg"
                  alt="Unity"
                  width={92}
                  height={32}
                />
                <Image
                  src="/images/tactsuit/tactglove-dk3/unreal.svg"
                  alt="Unreal Engine"
                  width={168}
                  height={32}
                />
                <Image
                  src="/images/tactsuit/tactglove-dk3/javascript.svg"
                  alt="JavaScript"
                  width={36}
                  height={40}
                />
                <Image
                  src="/images/tactsuit/tactglove-dk3/python.svg"
                  alt="Python"
                  width={40}
                  height={40}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const SpecBlock = ({
  label,
  value,
  caption,
  isLast,
}: {
  label: string;
  value: string;
  caption: string;
  /** 모바일 세로 정렬 시 마지막 항목은 border-b 제거. */
  isLast?: boolean;
}) => (
  <div
    className={cn(
      "flex flex-col gap-2 py-4",
      !isLast && "border-b border-coolgray-20",
      "laptop:flex-1 laptop:border-0 laptop:py-0",
    )}
  >
    <span className="whitespace-nowrap text-xs font-600 leading-4 tracking-[0.4px] text-coolgray-60 laptop:text-sm laptop:leading-[18px] laptop:tracking-[0.25px]">
      {label}
    </span>
    <span className="whitespace-nowrap text-base font-500 leading-[22px] tracking-[0.15px] text-coolgray-90 laptop:text-[22px] laptop:font-400 laptop:leading-7 laptop:tracking-normal">
      {value}
    </span>
    <span className="text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-60">
      {caption}
    </span>
  </div>
);
